import React from 'react';
import { useState } from 'react';
import { Github, Mail, Linkedin, FileText, ExternalLink, Cloud, ChevronDown } from 'lucide-react';
import {
  SiClaude,
  SiDocker,
  SiGithubactions,
  SiJavascript,
  SiNodedotjs,
  SiOpenai,
  SiPostgresql,
  SiPython,
  SiReact,
  SiTypescript,
} from 'react-icons/si';
import profilePic from './images/AaronLinkedinPFP.jpeg';
import allenImage from './images/allenimage.jpeg';
import freddyImage from './images/freddyimage.jpeg';
import backgroundImage from './images/RocketIMG.png';
import ivyImage from './images/ivyimage.png';

const App = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [expandedEssays, setExpandedEssays] = useState(() => new Set());

  const toggleEssay = (id) => {
    setExpandedEssays((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openEssay = (id) => {
    setExpandedEssays((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const resumeFilename = 'AaronMcCullough2026Resume.pdf';
  const resumeUrl = `${import.meta.env.BASE_URL}${resumeFilename}`;

  const handleResumeDownload = async () => {
    try {
      const response = await fetch(resumeUrl);
      if (!response.ok) {
        throw new Error('Resume request failed');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = resumeFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(resumeUrl, '_blank', 'noopener,noreferrer');
    }
  };
  
  const personalInfo = {
    name: "Aaron J. McCullough",
    title: "Software Engineer | Full-Stack Developer",
    bio: "Software Engineer with hands-on experience in full-stack web development and a strong foundation in data science principles. Proficient in HTML, CSS, JavaScript, and modern frameworks like React. Passionate about creating responsive, user-friendly interfaces and writing clean, efficient, and well-documented code.",
    email: "mccullougha00@yahoo.com",
    github: "https://github.com/AaronM0710",
    linkedin: "https://www.linkedin.com/in/aaronjmccullough",
    availableForWork: true,
    location: "Tampa, FL",
    workStyle: "Open to relocation",
  };

  const homeHighlights = [
    {
      label: "Coding Since",
      value: "2019",
      detail: "Years of steady hands-on learning and building across web development and software engineering.",
    },
    {
      label: "Performance Improvement",
      value: "75%",
      detail: "Cut page load times by resolving a major bottleneck at ZYSC LLC.",
    },
    {
      label: "Graduation",
      value: "May 2026",
      detail: "B.S. in Data Science & Analytics from the University of South Florida.",
    },
    {
      label: "Core Stack",
      value: "Architecture + AWS",
      detail: "Hands-on experience with software architecture, system design, CI/CD, infrastructure, APIs, PostgreSQL, AWS, Docker, and full-stack delivery.",
    },
  ];

  const aboutStrengths = [
    {
      title: "Full-Stack Execution",
      detail: "Comfortable contributing across frontend, backend, APIs, databases, and deployment workflows.",
    },
    {
      title: "Architecture & Infra",
      detail: "Interested in how systems are structured end-to-end, from software architecture and system design to infrastructure, environments, and reliability.",
    },
    {
      title: "Performance Mindset",
      detail: "Focused on debugging, optimization, and shipping improvements that create measurable user and business impact.",
    },
    {
      title: "Product Thinking",
      detail: "I like building software that is technically solid, useful to end users, and aligned with business goals.",
    },
    {
      title: "Growth-Oriented",
      detail: "Graduating in May 2026 and looking for environments where I can contribute quickly and keep leveling up.",
    },
  ];

  const testimonials = [
    {
      quote:
        "Working with Aaron at ZYSC LLC has been awesome. As a Frontend Developer, he is full of curiosity and always asking the right questions, the kind that push the project forward.",
      name: "Freddy Rojas",
      title: "System Engineer | Software Developer | n8n Automation",
      context: "Worked with Aaron on the same team at ZYSC LLC",
      image: freddyImage,
    },
    {
      quote:
        "Aaron has consistently impressed me with his charisma, helpfulness, and ability to bring people together. He does not just focus on his own success, he lifts others up and ensures that the entire team thrives.",
      name: "Ivy R. Gentry",
      title: "Administrative & Intake Specialist | Graduate Student",
      context: "Studied with Aaron and has known him across academic and professional settings",
      image: ivyImage,
    },
    {
      quote:
        "He truly understands architecture, performance, debugging, and writes code that is clean and easy for the rest of the team to work with. Any team that hires Aaron is getting someone who ships high-quality work quickly and levels up constantly.",
      name: "Allen Chen",
      title: "Custodial Cast Member @ Walt Disney World",
      context: "Studied data science with Aaron at the University of South Florida",
      image: allenImage,
    },
  ];

  const stackItems = [
    { name: "React", icon: SiReact, color: "#61DAFB" },
    { name: "TypeScript", icon: SiTypescript, color: "#3178C6" },
    { name: "JavaScript", icon: SiJavascript, color: "#F7DF1E" },
    { name: "Node.js", icon: SiNodedotjs, color: "#5FA04E" },
    { name: "Python", icon: SiPython, color: "#3776AB" },
    { name: "PostgreSQL", icon: SiPostgresql, color: "#4169E1" },
    { name: "AWS", icon: Cloud, color: "#FF9900" },
    { name: "Docker", icon: SiDocker, color: "#2496ED" },
    { name: "GitHub Actions", icon: SiGithubactions, color: "#2088FF" },
    { name: "Anthropic", icon: SiClaude, color: "#D97757" },
    { name: "OpenAI", icon: SiOpenai, color: "#10A37F" },
  ];

  const capstoneEssays = [
    {
      id: 'enhanced-biography',
      title: 'Enhanced Biography & Career Goals',
      subtitle: 'The Intersection of Data, Code, and Strategy: My Journey as an Information Professional',
      sections: [
        {
          body: 'My name is Aaron McCullough. I am a software engineer and a final-year student at the University of South Florida, pursuing a Bachelor of Science in Information Science with a concentration in Data Science & Analytics. My journey into the world of information technology was not just sparked by a fascination with code, but by a desire to understand how data can be harnessed to solve real-world problems. Living in the Tampa Bay area, I have witnessed the rapid growth of the local tech sector, which has further fueled my ambition to become a key player in this evolving landscape. As an information professional, I view myself as a bridge between complex technical systems and the human needs they serve. This essay outlines my educational foundation, my professional experiences in software engineering and AI, and my strategic goals for a future in technical leadership.',
        },
        {
          heading: 'Educational Foundation and BSIS Curriculum',
          body: 'My time in the BSIS program at USF has provided me with a dual-layered education that balances theoretical understanding with rigorous technical application. On the foundational level, I have explored the core tenets of Information Science, including information architecture, ethics, and policy. These courses challenged me to think critically about how information is organized, retrieved, and governed, ensuring that the systems I build are not only functional but also ethical and accessible.\n\nSimultaneously, my concentration in Data Science & Analytics has equipped me with a specialized toolkit for the modern data economy. Through coursework in visual analytics, data mining, and statistical modeling, I have learned to extract actionable insights from raw datasets. I have engaged with the full data lifecycle—from cleaning and preprocessing to advanced visualization and predictive analysis. This academic rigor is complemented by my membership in The National Society of Leadership and Success (NSLS), which has helped me refine the soft skills necessary to communicate complex data concepts to non-technical stakeholders.',
        },
        {
          heading: 'Technical Skills and Toolkit',
          body: 'In terms of technical proficiency, I have developed a diverse skillset that spans both software development and data analysis. My core programming competencies include Python, Java, and JavaScript (specifically React and TypeScript), which I use regularly in both my coursework and professional projects. On the data side, I am proficient in SQL for database management and have experience using R and Tableau for statistical analysis and visualization. Recently, I have focused heavily on integrating Artificial Intelligence into web applications, utilizing Large Language Models (LLMs) to create dynamic user experiences. I also hold the Google Data Analytics Professional Certificate, which served as an early catalyst for my deep dive into the field.',
        },
        {
          heading: 'Professional Experience and Projects',
          body: 'Transitioning from theory to practice, I have been working as a Software Engineer since April 2025. In this role, I apply my academic knowledge to production-level code, tackling challenges in front-end development and system architecture. This real-world experience has been invaluable, teaching me the importance of agile methodologies, version control, and collaborative debugging.\n\nBeyond my employment, I am an active builder in the "indie hacker" space. I am currently developing "Budget BOOM," an AI-powered budgeting application designed to help users manage their finances through intelligent automation. This project has allowed me to wear multiple hats—from product designer to full-stack developer—and has deepened my understanding of the product lifecycle. Additionally, I write "The AI Debrief," a newsletter where I curate and analyze the latest developments in artificial intelligence. This initiative not only keeps me on the cutting edge of industry trends but also serves as a platform to practice technical writing and community engagement.',
        },
        {
          heading: 'Career Goals and Future Outlook',
          body: 'Looking ahead, my career goals are centered on the convergence of software engineering and strategic leadership. Upon graduation in Spring 2026, I plan to continue advancing in my current trajectory as a software engineer, with a specific focus on AI integration and data-driven product development. In the short term, I aim to ship "Budget BOOM" to a wider user base and perhaps seek a specialized certification in cloud computing (such as AWS Solutions Architect) to bolster my infrastructure skills.\n\nLong-term, I envision myself transitioning into a technical leadership role, such as a Lead Engineer or Product Manager, where I can guide teams in building scalable, impactful solutions. To prepare for this transition, I have begun researching MBA programs, specifically targeting top-tier institutions that offer a strong focus on technology management. I believe that combining my deep technical background with advanced business acumen will position me to drive innovation effectively. Ultimately, my goal is to remain a lifelong learner, continuously adapting to the shifting sands of the technology landscape while delivering value as a principled information professional.',
        },
      ],
    },
    {
      id: 'project-management',
      title: 'Project Management Plan',
      subtitle: 'Software Tracking Database',
      sections: [
        {
          heading: 'Introduction',
          body: 'In a large-scale academic environment, such as a major research university, the volume of software assets, ranging from site-licensed operating systems to specialized departmental research tools, requires a robust management system. Currently, many institutions face challenges in tracking installation dates, licensing compliance, and version control across various departments. This project aims to develop a centralized software-tracking database for the IT Department. This database will serve as the single source of truth for software metadata, including developer information, license types (e.g., seat-based, concurrent, or site-wide), and the specific hardware on which the software is deployed. By implementing this system, the institution will mitigate legal risks associated with licensing audits and optimize its procurement spending.',
        },
        {
          heading: 'Phase 1: Initiation Phase',
          body: 'The primary goal of the initiation phase is to determine the feasibility of the project and secure formal approval. During this stage, the Project Manager (PM) will meet with the Chief Information Officer (CIO) and the IT Director to define the project’s boundaries. The main "deliverable" of this phase is a Project Proposal or Charter.',
          bullets: [
            { label: 'Activities', text: 'Conducting a needs assessment to identify gaps in current tracking methods and performing a cost-benefit analysis of building a custom database versus purchasing a commercial off-the-shelf solution.' },
            { label: 'Responsible Employees', text: 'Project Manager, IT Director, and a Financial Analyst.' },
            { label: 'Stakeholders', text: 'IT Administration and the University Procurement Office.' },
            { label: 'Decision-making', text: 'A "Go/No-Go" decision will be made by the Project Board based on the estimated ROI and resource availability.' },
          ],
        },
        {
          heading: 'Phase 2: Definition Phase',
          body: 'Once the project is approved, the definition phase focuses on gathering the specific requirements. According to Baars (2006), this phase involves determining what the project results must meet. This is critical for an educational setting where software requirements vary significantly between the Arts and Engineering departments.',
          bullets: [
            { label: 'Activities', text: 'Interviewing department heads to identify mandatory data fields (e.g., "installation date," "licensing agreement type") and determining user access levels.' },
            { label: 'Responsible Employees', text: 'Project Manager and Business Analysts.' },
            { label: 'Stakeholders', text: 'Departmental IT Liaisons and Faculty representatives.' },
            { label: 'Deliverables', text: 'A Functional Requirements Document (FRD) and a Project Result Description.' },
            { label: 'Assessment', text: 'Stakeholders will sign off on the requirements to prevent "scope creep" during later stages.' },
          ],
        },
        {
          heading: 'Phase 3: Design Phase',
          body: 'The design phase translates the requirements into a technical blueprint. For a tracking database, this involves designing the data schema to ensure that a single software version can be linked to multiple licenses and multiple physical machines.',
          bullets: [
            { label: 'Activities', text: 'Creating Entity-Relationship Diagrams (ERDs), designing user interface (UI) wireframes for IT staff, and selecting the technology stack (e.g., SQL-based architecture).' },
            { label: 'Responsible Employees', text: 'Database Architect and UI/UX Designer.' },
            { label: 'Deliverables', text: 'A Technical Design Document and a clickable prototype of the user interface.' },
            { label: 'Testing/Piloting', text: 'The PM will conduct a "design walk-through" with the IT technicians who will be the primary users to ensure the workflow is intuitive.' },
          ],
        },
        {
          heading: 'Phase 4: Development Phase',
          body: 'During the development phase, the actual construction of the database occurs. The project team builds the backend architecture and the frontend interface based on the designs approved in the previous phase.',
          bullets: [
            { label: 'Activities', text: 'Database table creation, coding the search and reporting functionalities, and integrating the database with existing university hardware inventory systems.' },
            { label: 'Responsible Employees', text: 'Software Developers and Database Administrators (DBAs).' },
            { label: 'Deliverables', text: 'A fully functional beta version of the software-tracking database.' },
            { label: 'Testing', text: 'Alpha testing is conducted by the development team to identify bugs, followed by internal IT unit testing to ensure data integrity.' },
          ],
        },
        {
          heading: 'Phase 5: Implementation Phase',
          body: 'The implementation phase involves the actual deployment of the database into the university’s live environment. This is often the most sensitive phase, as it requires moving data from legacy spreadsheets into the new system.',
          bullets: [
            { label: 'Activities', text: 'Data migration, installing the software on IT servers, and conducting training sessions for departmental IT coordinators.' },
            { label: 'Responsible Employees', text: 'Implementation Specialist, Project Manager, and IT Support Staff.' },
            { label: 'Stakeholders', text: 'All university departments that must report their software usage.' },
            { label: 'Pilot', text: 'A "soft launch" will occur within the College of Arts and Sciences to identify any real-world performance issues before a university-wide rollout.' },
          ],
        },
        {
          heading: 'Phase 6: Follow-up Phase',
          body: 'The final phase focuses on the long-term sustainability of the project and the formal closure of the development cycle. As Baars (2006) notes, this phase ensures that the project results are maintained and that the team reflects on the process.',
          bullets: [
            { label: 'Activities', text: 'Setting up a maintenance schedule, creating a "Help Desk" protocol for database issues, and conducting a Post-Implementation Review (PIR).' },
            { label: 'Responsible Employees', text: 'Project Manager and IT Maintenance Team.' },
            { label: 'Deliverables', text: 'Final Project Report, User Manuals, and a Project Evaluation.' },
            { label: 'Assessment', text: 'Success is measured against the original requirements, specifically, whether the IT department can successfully generate a licensing compliance report within five minutes.' },
          ],
        },
        {
          heading: 'Conclusion',
          body: 'Developing a software-tracking database for a university is a complex undertaking that requires strict adherence to project management principles to avoid budget overruns and technical failure. By following the six phases, Initiation, Definition, Design, Development, Implementation, and Follow-up, the project team can ensure that the final product meets the needs of all academic departments while providing the IT department with the tools necessary for effective institutional oversight. This structured approach provides the transparency needed for high-stakes decisions regarding the university’s digital assets.',
        },
        {
          heading: 'References',
          bullets: [
            { text: 'Baars, W. (2006). Project Management Handbook (Version 1.1). Data Archiving and Networked Services (DANS). https://www.projectmanagement-training.net/wp-content/uploads/2016/08/project_management_handbook.pdf' },
          ],
        },
      ],
    },
    {
      id: 'event-report',
      title: 'Event Report',
      subtitle: 'Future Ready Web – 2026 Trends · Attended February 25, 2026',
      sections: [
        {
          heading: 'Introduction',
          body: 'On February 25, 2026, I attended the "Future Ready Web - 2026 Trends Reshaping Design and Engineering" event hosted by Tampa Devs and the Pinellas Tech Network. Held at the Geographic Solutions HQ in Palm Harbor, this panel discussion brought together experts from across the industry including representatives from Intel, Kforce, and Taxwell to discuss the shifting paradigms in web development and information management. I attended this event to better understand how emerging architectures and AI-integrated workflows are redefining the role of the information professional in 2026.',
        },
        {
          heading: 'Event Content and Reflections',
          body: 'The panel explored three primary pillars currently reshaping the technical landscape: AI as a collaborator, the rise of composable architectures, and a shift toward human-focused, minimal design.\n\nThe discussion on AI as a collaborator was particularly insightful. The speakers, such as Alexis Menard (Intel) and Yukti Goyal (Kforce), emphasized that 2026 marks a transition where AI is no longer just a "copilot" for writing code but a true collaborator in the architectural phase. This challenged my previous view of AI as a simple efficiency tool. It was fascinating to hear how Adam Heaton (Taxwell) discussed AI’s role in attack surface management, highlighting that as our development tools become smarter, our responsibility for security and data integrity increases proportionally.\n\nThe segment on composable architectures resonated with my studies in information technology. The panel argued that modern builds are now defined by their ability to be modular and performant. This can be confusing when trying to balance complexity with speed, but the speakers clarified that "performance" in 2026 is measured by how seamlessly different data streams can be integrated without bloating the user experience. This reinforces the core principles of information studies: the effective, efficient organization and retrieval of data to meet a specific human need.',
        },
        {
          heading: 'Connection to Career Goals and Information Studies',
          body: 'This event was highly relevant to my current trajectory as I prepare to enter the professional world as a Software Engineer or Data Analyst. I am currently developing "Budget BOOM," a financial tracking application, and the insights regarding "organic, minimal, and human-focused experiences" have immediate applications for my project. Hearing David Rivera (Geographic Solutions) and Jeremy Rivera (Deque Systems) speak about design and accessibility reminded me that data is useless if it is not accessible or if the interface creates friction for the user.\n\nMy career goal is to work within the B2B SaaS or Med-Tech sectors, where the stakes for data accuracy and system performance are incredibly high. The trends discussed, specifically regarding AI collaboration and performance-driven builds, are the exact skills I need to master to remain competitive. In the Med-Tech space, a "composable architecture" allows for the modular integration of patient data, while "human-focused design" ensures that healthcare providers can make split-second decisions based on that data without being overwhelmed by a cluttered interface.\n\nFurthermore, the networking aspect of the event allowed me to connect with fellow tech leaders across Pinellas and Tampa Bay, grounding my academic knowledge in the local professional ecosystem. It confirmed that my proficiency in Java, Python, and SQL, combined with my focus on Data Analytics, aligns perfectly with the "future ready" requirements of 2026.',
        },
        {
          heading: 'Conclusion',
          body: 'Attending the Future Ready Web event provided a vital "reality check" for my transition from student to professional. It moved the concepts of AI and architecture from the theoretical realm of a textbook into a practical, high-stakes professional context. As I move forward with my capstone and my career, I plan to integrate these 2026 trends by focusing on modularity in my code and prioritizing the human element in my data visualizations. The information professional of 2026 must be more than just a coder; they must be a strategic architect who understands the synergy between AI, data performance, and the end-user experience.',
        },
        {
          heading: 'References',
          bullets: [
            { text: 'Tampa Devs. (2026, February 25). Future Ready Web - 2026 Trends Reshaping Design and Engineering. [Event]. Geographic Solutions, Inc., Palm Harbor, FL. https://www.meetup.com/tampadevs/' },
          ],
        },
      ],
    },
    {
      id: 'critical-thinking',
      title: 'Ethical Case Study Analysis',
      subtitle: 'The Global Nervous System: An Ethical Analysis of the Internet of Things and Bodies',
      sections: [
        {
          heading: 'Introduction: The Invisible Network',
          body: 'The Internet of Things (IoT) is often described as a "global nervous system," a vast, interconnected network of physical objects embedded with sensors, software, and other technologies designed to collect and exchange data (FwThinking, 2013). From smart thermostats that learn our schedules to industrial sensors that prevent power grid failures, IoT has fundamentally altered how we interact with the physical world. However, as this network extends from our homes into our very biology through the "Internet of Bodies" (IoB), the ethical stakes have shifted from mere data collection to the fundamental autonomy of the individual (Coleman, 2025). This essay evaluates the ethical landscape of IoT through a critical thinking lens, examining the tension between technological convenience and the right to biological and digital privacy.',
        },
        {
          heading: 'Ethical Implications: Privacy and the Quantified Self',
          body: 'The primary ethical concern regarding IoT lies in the "well-dressed privacy policy" of wearable technology. As Singer and Perry (2015) argue, wearables do more than track steps; they collect intimate biometric data that can be used to build comprehensive profiles of a person’s health and habits. The ethical risk here is "data persistence." Once this data is collected, it often exists indefinitely and can be sold to third parties, such as insurance companies or advertisers, without the user’s explicit understanding of the long-term consequences.\n\nColeman (2025) takes this a step further by introducing the concept of Infrastructure Literacy in the context of the Internet of Bodies. Unlike a smartphone that we can put down, IoB devices, such as pacemakers, insulin pumps, or smart contact lenses, are often internal or permanent. This creates a terrifying ethical vulnerability: if the infrastructure supporting these devices is compromised or if the privacy policies change, the individual’s literal physical safety and biological data are at risk. We must ask the critical question: Who truly "owns" the data generated by a human heart or a brain-computer interface?',
        },
        {
          heading: 'Policy Implications and Alternative Perspectives',
          body: 'While many focus on the dangers, Karsten (2016) offers an alternative perspective, suggesting that rigid, "one-size-fits-all" privacy policies might stifle the very innovation that could save lives. Policy implications for IoT require a delicate balance. We need "permissionless innovation" to allow technology to thrive, but this must be tempered with "privacy by design." Current policies often rely on "notice and consent" (those long terms and conditions we all skip), which the Foundation for Critical Thinking (n.d.) might describe as a failure of clarity and depth. A truly ethical policy would move toward "Infrastructure Literacy," where users aren’t just consenting to data collection but are educated on the systems that manage their most intimate information (Coleman, 2025).',
        },
        {
          heading: 'Future Career: The Information Professional’s Responsibility',
          body: 'As a future information professional, particularly in the realms of data analytics and software development, these ethical challenges are not theoretical; they are architectural. My role will involve making choices about data retention, encryption, and transparency. Applying critical thinking means I must reject the assumption that more data is always better. Instead, I must apply the intellectual standard of precision to ensure that only the necessary data is collected and that the systems I build are resilient against the misuse of behavioral data. The responsibility shifts from merely "making things work" to ensuring that the "things" we make do not become tools for surveillance or biological exploitation.',
        },
        {
          heading: 'Suggestions, Critiques, and Recommendations',
          body: 'To face these challenges, I recommend a shift from "Consumer Literacy" to "Infrastructure Literacy." We need to critique the current model of data extraction, which treats human behavior as a raw resource.',
          bullets: [
            { label: 'Transparency', text: 'Companies should be required to provide "Nutritional Labels" for IoT devices, clearly stating what data is collected and where it goes.' },
            { label: 'Data Sovereignty', text: 'Users should have the right to delete their data or move it between services (data portability).' },
            { label: 'Security Mandates', text: 'Basic security standards for IoT devices should be legally required to prevent the "botnet" exploitation of insecure home devices.' },
          ],
        },
        {
          heading: 'Reflection on Critical Thinking',
          body: 'In analyzing this case, I applied the "Five Tips to Improve Your Critical Thinking" (Agoos, 2016). I started by formulating my question: Does the benefit of IoT outweigh the risk to individual autonomy? I gathered information from both historical perspectives (FwThinking, 2013) and modern critiques (Coleman, 2025). I applied the information by looking at the "points of view" of both the innovators and the privacy advocates. Most importantly, I considered the implications, realizing that the "Internet of Bodies" means that a security breach is no longer just a digital problem, but a physical one. By questioning the validity of the authority of tech companies to own our biological data, I moved from a "novice" acceptance of technology to a "proficient" critical evaluation.',
        },
        {
          heading: 'Conclusion',
          body: 'The Internet of Things and the Internet of Bodies represent the next frontier of human evolution, but they also represent a significant ethical crossroads. While the convenience and health benefits are undeniable, the risks to privacy and autonomy are profound. By applying rigorous critical thinking and demanding better infrastructure literacy, we can ensure that these technologies enhance our lives without harvesting our identities. As we build this global nervous system, we must ensure it is one that respects the dignity and privacy of every individual it connects.',
        },
        {
          heading: 'References',
          bullets: [
            { text: 'Agoos, S. [TED-Ed]. (2016, March 15). 5 tips to improve your critical thinking [Video]. YouTube. https://ed.ted.com/lessons/5-tips-to-improve-your-critical-thinking-samantha-agoos' },
            { text: 'Coleman, A. (2025). Infrastructure literacy: Understanding the internet of bodies. Infophilia. https://infophilia.substack.com/p/infrastructure-literacy' },
            { text: 'Foundation for Critical Thinking. (n.d.). Defining critical thinking. http://www.criticalthinking.org/pages/defining-critical-thinking/766' },
            { text: 'FwThinking. (2013, November 18). What is the internet of things? [Video]. TedEd. http://ed.ted.com/on/VGdKwYzz#watch' },
            { text: 'Karsten, J. (2016, March 25). Alternative perspectives on the internet of things. Brookings. https://www.brookings.edu/blog/techtank/2016/03/25/alternative-perspectives-on-the-internet-of-things/' },
            { text: 'Singer, R. W., & Perry, A. J. (2015). Wearables: The well-dressed privacy policy. Intellectual Property & Technology Law Journal, 27(7), 24-27.' },
          ],
        },
      ],
    },
    {
      id: 'information-policy',
      title: 'Information/Technology in the News',
      subtitle: 'Regulating Agentic AI: The Policy Gap Between Static Rules and Autonomous Systems',
      sections: [
        {
          body: 'In October of 2025, the Association for Computing Machinery’s Europe Technology Policy Committee released a policy brief which was titled “Systemic Risks Associated with Agentic AI” (Bellogín et al., 2025). The report was about a type of artificial intelligence system that can perceive its environment, set goals, and complete tasks with a very small amount of human oversight. Unlike earlier AI tools that simply responded to users prompts, agentic AI systems operate continuously and autonomously, raising questions that existing regulatory frameworks were not designed for. Within just a few months of the release of the report, the Trump admin issued an executive order in March of 2026 that proposed federal preemption of state AI laws, adding another layer of complexity to a policy landscape already struggling to keep pace with the technology.\n\nRegulators classify AI one way; agentic systems behave another. The EU AI Act of 2025 assigns risk categories to AI systems at the time of deployment. The ACM’s policy brief argues that this approach fails to account for systems that evolve after deployment, interact with other autonomous agents, and produce outcomes their developers did not anticipate (Bellogín et al., 2025). A system approved as low-risk today could behave as a high-risk system tomorrow if it learns new behaviors or encounters new data. The brief calls this a “fundamental” gap in oversight, because the governance model assumes AI is a static product rather than a dynamic process.\n\nCongress has no federal AI law on the books. California and Colorado filled that gap with their own laws in 2025. Cali’s AI Safety Act protects employees from retaliation after reporting AI related risks. Colorado went with a different approach and now requires developers and deployers of high-risk systems to curb algorithmic discrimination. However, the Trump administration’s March 2026 National AI Policy Framework directed the Department of Justice to challenge state AI laws deemed overly burdensome and authorized the withholding of federal broadband funding from states that resist (King & Spalding, 2026). The administration framed its approach as necessary to maintain U.S. competitiveness, arguing that a patchwork of state regulations slows AI deployment and investment.\n\nOpenAI, Google, and Meta all want federal preemption because it lets them follow one set of rules instead of fifty. The FTC and FDA would become sector specific regulators under that framework. California and Colorado legislators see the tradeoff which is that federal preemption guts the consumer protection laws they passed. Workers and consumers bear the cost if oversight weakens. The person denied a loan by an algo or screened out from a potential job application by an automated system has less ways to challenge these decisions. Information professionals sit in between these groups. They run the data pipelines and documentation practices that make AI systems auditable in the first place and allows them to be held accountable.\n\nThe ACM brief makes a distinction between regulatory foresight and societal foresight that is useful for understanding the full scope of the problem (Bellogín et al., 2025). Regulatory foresight covers the creation of compliance frameworks and enforceable standards. Societal foresight examines how people adapt their behavior as autonomous systems become common in daily life. When a machine mirrors your tone or pauses before answering, people trust it more and begin assigning it a personality. When agentic AI systems engage in long-term interactions with users, they can create emotional dependence and erode the boundaries between human and machine relationships. This is a concern that no current U.S. legislation addresses directly.\n\nFederal preemption before federal standards exist leaves no one watching the systems. The executive order treats deployment speed as the priority and the administration calls state laws “burdensome”, pushing past algorithmic discrimination and consumer harm. A state by state patchwork does cost companies that operate in all 50 states so some federal coordination makes sense. The ACM proposed dynamic governance: oversight that adapts as systems evolve instead of locking in a single risk assessment at deployment. Regulators would need to build technical capacity and maintain ongoing relationships with developers in order to make that work. It fits how agentic AI operates better than any static framework. If you work in data governance, you’ll need audit trails that track how agentic systems modify their own behavior over time. Regulators reconstructing why an AI system made a specific decision will depend on the metadata and documentation standards that you maintain. Librarians and information scientists running public facing tools will need transparency and disclosure policies they can point to. The ACM’s call for accountability and explainability maps onto what information science has argued for decades which is the fact that people should be able to access and challenge the systems that shape their lives. Agentic AI is moving into hiring platforms, medical diagnostics and financial services. The people who understand both the technical infra and whats at stake for the humans on the other end need to be in the room when regulators write the rules.',
        },
        {
          heading: 'References',
          bullets: [
            { text: 'Bellogín, A., Giudici, P., Larsson, S., Pang, J., Schimpf, G., Sengupta, B., & Solmaz, G. (2025). Systemic risks associated with agentic AI: A policy brief. Association for Computing Machinery. https://www.acm.org/binaries/content/assets/public-policy/europe-tpc/systemic_risks_agentic_ai_policy-brief_final.pdf' },
            { text: 'Greenberg Traurig. (2025, December). 2026 outlook: Artificial intelligence. Greenberg Traurig Insights. https://www.gtlaw.com/en/insights/2025/12/2026-outlook-artificial-intelligence' },
            { text: 'King & Spalding. (2026, January). New state AI laws are effective on January 1, 2026, but a new executive order signals disruption. King & Spalding Client Alert. https://www.kslaw.com/news-and-insights/new-state-ai-laws-are-effective-on-january-1-2026-but-a-new-executive-order-signals-disruption' },
          ],
        },
      ],
    },
    {
      id: 'program-review',
      title: 'Professional Reflection',
      subtitle: 'The BSIS Journey',
      sections: [
        {
          heading: 'Introduction',
          body: 'I graduate from USF’s Bachelor of Science in Information Science program in May 2026, with a concentration in Data Analytics. I came into the program thinking of engineering as feature-building. I’m leaving with a broader view that includes database design, statistical reasoning, and the project management work that turns code into something a team can ship. The courses and assignments below are the ones that shaped that shift.',
        },
        {
          heading: 'Overview of Courses',
          body: 'LIS 4930: Project Management gave me the vocabulary I now use in my current role. We covered SDLC phases, Scrum methodologies, resource planning, and how to scope work against stakeholder constraints. Since I was taking this course while working my first role as a developer, it was perfect timing for me. I was running real sprints at my day job while reading about Agile in the textbook, and the two sides reinforced each other. Before the course, I treated JIRA tickets as assignments that showed up in my queue. From this class learned how a sprint gets planned, why story pointing matters and how a product owner trades engineering bandwidth against business pressure. What I learned in that course directly effects how I read sprint retros and plan my own work in my current role as a software engineer.\n\nSTA 2023: Introductory Statistics I sits at the 2000 level, but it did more for my concentration than most upper-division classes. Data analytics without statistics reduces to pattern-matching on noise. The course taught me about sampling bias, confidence intervals, and what a p-value actually means. I leaned on that grounding in my senior capstone when my team discovered our dataset had near-zero feature-to-target correlations. Without the statistics background, I might have reported a model accuracy number and moved on. With it, I knew to investigate why the features weren’t predictive and document what we found.',
        },
        {
          heading: 'Overview of Assignments',
          body: 'During the course of the BSIS program, there were two specific assignments that had a big impact on my development as an information professional. The first was the Needs Identification and Project Plan for the Museum of Science & Industry in Tampa. I walked through the institution’s existing technology stack and identified gaps where their systems were limiting the educational work that they were trying to do. That assignment taught me something my pure coding classes hadn’t: the stack you use comes after the mission, not the other way around. I now ask what an organization is trying to accomplish before I even think about which framework to use.\n\nThe second was my senior capstone, "Multi-Factor Predictive Modeling of Consumer Credit Risk," which I built with teammates Kelsey Appell and Patrick Aabram. We trained regression and classification models against a synthetic dataset and found that the features correlated at near-zero with the target variable. Rather than fill the report with inflated accuracy claims, we documented the data quality issue and explained why our models couldn’t outperform baseline. Writing that report was harder than writing a success story would’ve been, but it matched how real work goes: sometimes the honest finding is that the data is broken. Alongside the capstone, I kept building BudgetBoom, my AI-powered budgeting app, on React, TypeScript, PostgreSQL, and OpenAI. The problems I hit there (schema migrations, front-end state, API rate limits) lined up with the problems I hit at work.',
        },
        {
          heading: 'Conclusion',
          body: 'The BSIS program at USF has been transformative, taking me from a foundational interest in technology to a professional-grade competency in Data Analytics and Information Science. What I have learned most significantly is that technology does not exist in a vacuum. It is a tool meant to serve human and organizational needs. This program has prepared me for my career by teaching me to approach technical problems with an information first mindset. Whether I am drafting a project plan or debugging a React component, now I look at the bigger picture when it comes to data accessibility and system efficiency. As I transition fully into the professional world, the combination of technical rigor and strategic theory I gained here will serve as a permanent foundation for my growth as a Software Engineer.',
        },
      ],
    },
  ];

  const projects = [
    {
      title: "ZYSC LLC — Web Application",
      description: "Full-stack web application built from the ground up. Diagnosed and resolved a critical performance bottleneck by implementing Gunicorn with multi-threaded workers, cutting page load times by up to 75%. Contributed across application architecture, system design decisions, API integrations, CI/CD and DevOps workflows, infrastructure-minded deployment work, and user-facing features.",
      technologies: ["React", "Python", "Gunicorn", "Docker", "CI/CD", "Infrastructure", "System Design", "DevOps", "REST APIs", "Full-Stack", "Deployment"],
      github: null,
      demo: null,
      note: "NDA — Private Repository",
      companyUrl: "https://www.zyscsoftware.com/",
      companyLabel: "View Company Site",
      workGithub: "https://github.com/aaronzysc",
      workGithubLabel: "View Commits (Work GitHub)",
    },
    {
      title: "AI Budgeting App",
      description: "AI-powered personal finance tool with OCR receipt scanning via AWS Textract, GPT-4 driven spending insights, and PostgreSQL-backed transaction tracking. Features user authentication and document upload.",
      technologies: ["TypeScript", "React", "Node.js", "PostgreSQL", "AWS Textract", "OpenAI GPT-4"],
      github: "https://github.com/AaronM0710/ai-budgeting-app",
      demo: "https://budgetboom.io/home",
    },
    {
      title: "Security Landing Page",
      description: "A professional landing page developed for a security company, featuring modern design and responsive layout.",
      technologies: ["React", "Tailwind CSS", "Responsive Design", "UI/UX"],
      github: "https://github.com/AaronM0710/security-landing",
      demo: "https://aaronm0710.github.io/security-landing/",
    },
    {
      title: "Interactive Drum Machine",
      description: "A web-based drum machine application that allows users to create and play beats in real-time.",
      technologies: ["React", "Web Audio API", "CSS", "JavaScript"],
      github: "https://github.com/AaronM0710/FCC-drum-machine",
      demo: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    },
  ];

  const renderHome = () => (
    <div className="flex min-h-[calc(100vh-5rem)] items-center py-8">
      <div className="w-full space-y-8 rounded-2xl border border-gray-700 bg-gray-900/85 p-6 shadow-xl backdrop-blur-sm sm:p-8 md:p-10">
            <div className="grid gap-8 lg:grid-cols-[auto_1fr] lg:items-start">
              <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
                <img 
                  src={profilePic} 
                  alt="Profile" 
                  className="h-28 w-28 rounded-full border-4 border-gray-700 object-cover sm:h-32 sm:w-32 md:h-36 md:w-36"
                />
              </div>

              <div className="space-y-5 text-center lg:text-left">
                <div className="space-y-3">
                  <p className="text-sm font-medium uppercase tracking-[0.25em] text-blue-300">
                    Software Engineer based in Tampa
                  </p>
                  <h1 className="text-4xl font-bold leading-tight text-gray-100 sm:text-5xl">
                    {personalInfo.name}
                  </h1>
                  <h2 className="text-xl text-gray-300 sm:text-2xl">{personalInfo.title}</h2>
                  <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-gray-300 lg:justify-start">
                    <span className="rounded-full border border-gray-600 px-3 py-1">
                      {`📍 ${personalInfo.location}`}
                    </span>
                    <span className="rounded-full border border-gray-600 px-3 py-1">
                      {personalInfo.workStyle}
                    </span>
                    <span className="relocation-badge rounded-full border border-amber-300/40 px-3 py-1 text-amber-100 shadow-lg shadow-amber-500/10">
                      New Grad May 2026
                    </span>
                    {personalInfo.availableForWork && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-green-500/40 px-3 py-1 text-green-400">
                        <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse"></span>
                        Available for work
                      </span>
                    )}
                  </div>
                </div>

                <p className="max-w-3xl text-base leading-8 text-gray-300 sm:text-lg">
                  {personalInfo.bio}
                </p>

                <div className="flex flex-wrap justify-center gap-3 lg:justify-start">
                  {["React", "TypeScript", "Node.js", "AWS", "Docker", "CI/CD", "Infrastructure", "System Design", "OpenAI"].map((skill) => (
                    <span key={skill} className="rounded-full bg-gray-800 px-3 py-1 text-sm text-gray-200 ring-1 ring-gray-700">
                      {skill}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap justify-center gap-4 lg:justify-start">
                  <a href={personalInfo.github} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-gray-400 hover:text-gray-200">
                    <Github size={20} />
                    <span>Personal GitHub</span>
                  </a>
                  <a href="https://github.com/aaronzysc" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-gray-400 hover:text-gray-200">
                    <Github size={20} />
                    <span>Work GitHub</span>
                  </a>
                  <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-gray-400 hover:text-gray-200">
                    <Linkedin size={20} />
                    <span>LinkedIn</span>
                  </a>
                  <a href={`mailto:${personalInfo.email}`} className="flex items-center space-x-2 text-gray-400 hover:text-gray-200">
                    <Mail size={20} />
                    <span>Email</span>
                  </a>
                </div>
              </div>
          </div>
        </div>
      </div>
  );

  const renderPortfolio = () => (
    <div className="py-8">
      <h2 className="text-2xl font-bold text-gray-100 mb-6">Projects</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {projects.map((project, index) => (
          <div key={index} className="border border-gray-700 rounded-lg p-6 bg-gray-800 shadow-sm hover:shadow-lg hover:border-gray-500 transition-all duration-200">
            <h3 className="text-xl font-semibold mb-2 text-gray-100">{project.title}</h3>
            {project.note && (
              <span className="inline-block text-xs text-yellow-400 border border-yellow-600 rounded px-2 py-0.5 mb-2">
                {project.note}
              </span>
            )}
            <p className="text-gray-400 mb-4">{project.description}</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {project.technologies.map((tech, i) => (
                <span key={i} className="bg-gray-700 px-3 py-1 rounded-full text-sm text-gray-300">
                  {tech}
                </span>
              ))}
            </div>
            <div className="flex space-x-4">
              {project.github && (
                <a href={project.github} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-gray-400 hover:text-gray-200">
                  <Github size={16} />
                  <span>Code</span>
                </a>
              )}
              {project.demo && (
                <a href={project.demo} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-gray-400 hover:text-gray-200">
                  <ExternalLink size={16} />
                  <span>Demo</span>
                </a>
              )}
              {project.companyUrl && (
                <a href={project.companyUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-gray-400 hover:text-gray-200">
                  <ExternalLink size={16} />
                  <span>{project.companyLabel || 'Company Site'}</span>
                </a>
              )}
              {project.workGithub && (
                <a href={project.workGithub} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-gray-400 hover:text-gray-200">
                  <Github size={16} />
                  <span>{project.workGithubLabel || 'Work GitHub'}</span>
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAbout = () => (
    <div className="py-8">
      <div className="space-y-8 rounded-2xl border border-gray-700 bg-gray-800 p-6 shadow-xl sm:p-8">
        <section className="space-y-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-100">About Me</h2>
            <p className="mt-2 max-w-3xl text-gray-400">
              I’m a software engineer who enjoys building practical, well-crafted products and getting closer to the kind of work that creates real value for users and teams.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {homeHighlights.map((highlight) => (
              <div key={highlight.label} className="rounded-xl border border-gray-700 bg-gray-900/70 p-5">
                <p className="text-sm uppercase tracking-wide text-gray-400">{highlight.label}</p>
                <p className="mt-2 text-2xl font-bold text-gray-100">{highlight.value}</p>
                <p className="mt-2 text-sm leading-6 text-gray-300">{highlight.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4 border-t border-gray-700 pt-8">
          <h3 className="text-2xl font-semibold text-gray-100">What I Bring</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {aboutStrengths.map((strength) => (
              <div key={strength.title} className="rounded-xl border border-gray-700 bg-gray-900/50 p-5">
                <h4 className="text-lg font-semibold text-gray-100">{strength.title}</h4>
                <p className="mt-2 text-sm leading-6 text-gray-300">{strength.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4 border-t border-gray-700 pt-8">
          <h3 className="text-2xl font-semibold text-gray-100">Professional Biography</h3>
          <div className="space-y-4 text-gray-300">
            <p className="leading-8">
              Since I started learning to code in 2019, I have been drawn to the process of turning ideas into working software. That interest grew into professional experience at ZYSC LLC, where I now contribute to production web applications in an Agile/Scrum environment. My work has included application development, API integrations, debugging, QA, performance tuning, and DevOps-oriented workflows that support deployment, CI/CD, infrastructure, system design thinking, and reliability.
            </p>
            <p className="leading-8">
              I am especially motivated by problems that require both technical depth and practical judgment. In real terms, that has meant identifying bottlenecks, improving reliability, supporting deployment workflows, thinking through software architecture and system design decisions, and shipping features that help the business move forward. One example was resolving a major performance issue that helped reduce page load times by up to 75 percent. Another was building my AI budgeting app end-to-end using React, TypeScript, PostgreSQL, AWS, and OpenAI-powered workflows.
            </p>
            <p className="leading-8">
              As I approach graduation from the University of South Florida in May 2026, I am looking for software engineering opportunities where I can contribute early, keep learning from strong teams, and continue growing into a high-impact engineer. I am based in Tampa, open to relocation, and particularly interested in roles involving full-stack development, software architecture, system design, CI/CD pipelines, cloud infrastructure, APIs, Docker, DevOps, and AI-enabled products.
            </p>
          </div>
        </section>

        <section className="space-y-5 border-t border-gray-700 pt-8">
          <div>
            <h3 className="text-2xl font-semibold text-gray-100">My Stack</h3>
            <p className="mt-2 max-w-3xl text-gray-400">
              The technologies I use most across product development, architecture, deployment, and AI-driven features.
            </p>
          </div>

          <div className="stack-marquee overflow-hidden rounded-2xl border border-gray-700 bg-gray-900/60 py-5">
            <div className="stack-track flex w-max items-center gap-4 px-4">
              {[...stackItems, ...stackItems].map((item, index) => (
                <div
                  key={`${item.name}-${index}`}
                  className="flex min-w-[190px] items-center gap-4 rounded-2xl border border-gray-700 bg-gray-800/90 px-4 py-3 shadow-lg"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-950/70 shadow-md ring-1 ring-gray-700">
                    <item.icon size={28} style={{ color: item.color }} aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-[0.18em] text-gray-500">Tech</p>
                    <p className="text-base font-semibold text-gray-100">{item.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );

  const renderTestimonials = () => (
    <div className="py-8">
      <div className="space-y-8 rounded-2xl border border-gray-700 bg-gray-800 p-6 shadow-xl sm:p-8">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-100">Testimonials</h2>
            <p className="mt-2 max-w-3xl text-gray-400">
              Excerpts from LinkedIn recommendations from people who have worked with me and know how I show up as a teammate.
            </p>
          </div>
          <a
            href={personalInfo.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200"
          >
            View LinkedIn
            <ExternalLink size={14} />
          </a>
        </div>

        <div className="grid gap-4">
          {testimonials.map((testimonial) => (
            <div key={testimonial.name} className="rounded-xl border border-gray-700 bg-gray-900/50 p-6">
              <p className="text-lg leading-8 text-gray-200">"{testimonial.quote}"</p>
              <div className="mt-5 flex items-center gap-4">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="h-14 w-14 rounded-full object-cover ring-2 ring-gray-700"
                />
                <div className="space-y-1">
                  <p className="font-semibold text-gray-100">{testimonial.name}</p>
                  <p className="text-sm text-gray-400">{testimonial.title}</p>
                  <p className="text-sm text-gray-500">{testimonial.context}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCapstone = () => (
    <div className="py-8">
      <div className="space-y-8 rounded-2xl border border-gray-700 bg-gray-800 p-6 shadow-xl sm:p-8">
        <header className="space-y-3 border-b border-gray-700 pb-6">
          <p className="text-sm font-medium uppercase tracking-[0.25em] text-blue-300">
            University of South Florida
          </p>
          <h2 className="text-3xl font-bold text-gray-100">BSIS Senior Capstone</h2>
          <p className="max-w-3xl text-gray-400">
            A consolidated portfolio of the deliverables required for the BSIS Senior Capstone course: bio, extended bio, resume, and the reflective essays from the program.
          </p>
        </header>

        <section className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-100">Deliverables</h3>
          <ul className="grid gap-2 text-gray-300 sm:grid-cols-2">
            <li>
              <button
                type="button"
                onClick={() => setActiveSection('home')}
                className="text-blue-300 hover:text-blue-200"
              >
                Bio &rarr; Home
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => setActiveSection('resume')}
                className="text-blue-300 hover:text-blue-200"
              >
                Resume &rarr; Resume
              </button>
            </li>
            {capstoneEssays.map((essay) => (
              <li key={essay.id}>
                <button
                  type="button"
                  onClick={() => openEssay(essay.id)}
                  className="text-left text-blue-300 hover:text-blue-200"
                >
                  {essay.title}
                </button>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-3 border-t border-gray-700 pt-8">
          {capstoneEssays.map((essay) => {
            const isExpanded = expandedEssays.has(essay.id);
            return (
              <section
                key={essay.id}
                id={essay.id}
                className="overflow-hidden rounded-xl border border-gray-700 bg-gray-900/40 scroll-mt-24"
              >
                <button
                  type="button"
                  onClick={() => toggleEssay(essay.id)}
                  aria-expanded={isExpanded}
                  className="flex w-full items-center justify-between gap-4 p-5 text-left transition-colors hover:bg-gray-900/70"
                >
                  <div className="min-w-0">
                    <h3 className="text-xl font-semibold text-gray-100">{essay.title}</h3>
                    {essay.subtitle && (
                      <p className="mt-1 text-sm italic text-gray-400">{essay.subtitle}</p>
                    )}
                  </div>
                  <ChevronDown
                    size={22}
                    className={`shrink-0 text-gray-400 transition-transform duration-200 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isExpanded && (
                  <div className="border-t border-gray-700 p-5">
                    {essay.sections ? (
                      <div className="space-y-6 text-gray-300">
                        {essay.sections.map((section, idx) => (
                          <div key={idx} className="space-y-3">
                            {section.heading && (
                              <h4 className="text-lg font-semibold text-gray-100">{section.heading}</h4>
                            )}
                            {section.body && (
                              <div className="space-y-4 whitespace-pre-line leading-8">
                                {section.body}
                              </div>
                            )}
                            {section.bullets && (
                              <ul className="list-disc space-y-2 pl-6 leading-8">
                                {section.bullets.map((bullet, i) => (
                                  <li key={i}>
                                    {bullet.label ? (
                                      <>
                                        <strong className="text-gray-100">{bullet.label}:</strong>{' '}
                                        {bullet.text}
                                      </>
                                    ) : (
                                      bullet.text
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4 whitespace-pre-line text-base leading-8 text-gray-300">
                        {essay.content}
                      </div>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderResume = () => (
    <div className="space-y-6 bg-gray-800 p-6 rounded-lg">
      {/* Header with Download Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-100">Resume</h2>
        <button
          type="button"
          onClick={handleResumeDownload}
          className="flex items-center space-x-2 bg-gray-700 px-4 py-2 rounded-lg hover:bg-gray-600 text-gray-200"
        >
          <FileText size={20} />
          <span>Download PDF</span>
        </button>
      </div>

      {/* Education Section */}
      <section className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-100 border-b border-gray-700 pb-2">Education</h3>
        <div>
          <h4 className="font-medium text-gray-200">University of South Florida</h4>
          <p className="text-gray-400">Major: Information Science (concentration in Data Science &amp; Analytics)</p>
          <p className="text-gray-400">Expected Graduation: May 2026</p>
          <p className="text-gray-400">GPA: 3.0</p>

          <div className="mt-4">
            <h5 className="text-gray-300 font-medium">Relevant Courses</h5>
            <ul className="grid grid-cols-2 gap-2 mt-2 text-gray-400">
              <li>• Python Programming</li>
              <li>• Java I & II</li>
              <li>• Database Concepts</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-100 border-b border-gray-700 pb-2">Certifications</h3>
        <div className="flex flex-wrap gap-3">
          <a
            href="https://www.freecodecamp.org/certification/AaronM0710/responsive-web-design"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-full text-sm text-gray-300 transition-colors"
          >
            Responsive Web Design
            <ExternalLink size={12} />
          </a>
          <a
            href="https://www.freecodecamp.org/certification/AaronM0710/javascript-algorithms-and-data-structures"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-full text-sm text-gray-300 transition-colors"
          >
            JavaScript Algorithms and Data Structures
            <ExternalLink size={12} />
          </a>
        </div>
      </section>

      {/* Work Experience Section */}
      <section className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-100 border-b border-gray-700 pb-2">Work Experience</h3>
        
        {/* Software Engineer - ZYSC LLC */}
        <div className="space-y-2">
          <div className="flex justify-between">
            <h4 className="font-medium text-gray-200">Software Engineer</h4>
            <p className="text-gray-400">Apr 2025 - Present</p>
          </div>
          <p className="text-gray-300">ZYSC LLC</p>
          <ul className="list-disc ml-6 text-gray-400">
            <li>Contributed to the development of a new web application from scratch, including front-end, back-end, and deployment processes</li>
            <li>Diagnosed and resolved critical performance bottleneck that plagued the application for months, implementing Gunicorn with multi-threaded workers to improve page load times by up to 75%</li>
            <li>Built hands-on experience with Docker, CI/CD practices, system design thinking, and infrastructure-minded DevOps workflows while supporting deployment, environment setup, and overall application reliability</li>
            <li>Maintained and improved a second codebase, ensuring optimal performance and functionality for the company website</li>
            <li>Collaborated closely with a back-end team on API integrations and data flow, and with the marketing team to implement new features and user-facing updates</li>
          </ul>
        </div>

        {/* Security Officer - Tampa General */}
        <div className="space-y-2">
          <div className="flex justify-between">
            <h4 className="font-medium text-gray-200">Security Officer</h4>
            <p className="text-gray-400">Oct 2024 - Apr 2025</p>
          </div>
          <p className="text-gray-300">Tampa General Hospital</p>
          <ul className="list-disc ml-6 text-gray-400">
            <li>Conducted weapon screenings and maintained a secure environment through regular patrols, both on foot inside the facility and in a vehicle around the premises</li>
            <li>Responded to emergency situations, including Code Grey and other critical alerts, demonstrating the ability to remain calm and decisive under pressure</li>
            <li>Maintained a high level of situational awareness to ensure the safety of staff, patients, and visitors</li>
          </ul>
        </div>

        {/* Web Developer Intern */}
        <div className="space-y-2">
          <div className="flex justify-between">
            <h4 className="font-medium text-gray-200">Web Developer Intern</h4>
            <p className="text-gray-400">Jan 2024 - May 2024</p>
          </div>
          <p className="text-gray-300">USF Global Tech Experience</p>
          <ul className="list-disc ml-6 text-gray-400">
            <li>Developed responsive websites in a collaborative team environment using HTML, CSS, JavaScript, Bootstrap, and Tailwind CSS</li>
            <li>Built user-friendly web interfaces that display correctly across multiple devices and screen sizes</li>
            <li>Implemented responsive design principles to ensure optimal viewing experience across desktop and mobile platforms</li>
            <li>Applied version control systems and collaborative development workflows to meet project deadlines</li>
          </ul>
        </div>

        {/* Bouncer / Armed Security */}
        <div className="space-y-2">
          <div className="flex justify-between">
            <h4 className="font-medium text-gray-200">Bouncer / Armed Security Officer</h4>
            <p className="text-gray-400">Sep 2022 - Jul 2024</p>
          </div>
          <p className="text-gray-300">Gulf Coast Security Services</p>
          <ul className="list-disc ml-6 text-gray-400">
            <li>Redesigned company website to make it more responsive and user friendly</li>
            <li>Maintained zero incidents of theft or vandalism on the premises</li>
            <li>Successfully collaborated with local law enforcement, aiding in the resolution of two significant cases</li>
            <li>Conducted security patrols, ensuring that all areas were secure and free from unauthorized access</li>
          </ul>
        </div>

        {/* Head Lifeguard */}
        <div className="space-y-2">
          <div className="flex justify-between">
            <h4 className="font-medium text-gray-200">Head Lifeguard & Water Safety Instructor</h4>
            <p className="text-gray-400">May 2018 - Aug 2023</p>
          </div>
          <p className="text-gray-300">City of Clearwater</p>
          <ul className="list-disc ml-6 text-gray-400">
            <li>Taught hundreds of kids how to swim and performed lifesaving water rescues</li>
            <li>Trained and supervised new lifeguards, ensuring they followed all safety protocols and procedures</li>
            <li>Conducted routine pool maintenance tasks, including chemical testing and cleaning</li>
            <li>Reduced the number of pool related incidents by 25%</li>
          </ul>
        </div>
      </section>

      {/* Skills Section */}
      <section className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-100 border-b border-gray-700 pb-2">Skills</h3>
        <div className="flex flex-wrap gap-2">
          {["Python", "R", "SQL", "Java", "HTML", "CSS", "JavaScript", "TypeScript", "React", "Redux", "Bootstrap", "Tailwind CSS", "Git", "GitHub", "Docker", "CI/CD", "DevOps", "AWS", "Infrastructure", "Software Architecture", "System Design", "Tableau", "Excel", "Data Visualization", "Database Management", "RESTful APIs", "Responsive Web Design", "Full-Stack Development", "API Integration", "Statistical Analysis"].map((skill, index) => (
            <span key={index} className="bg-gray-700 px-3 py-1 rounded-full text-sm text-gray-300">
              {skill}
            </span>
          ))}
        </div>
      </section>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {activeSection === 'home' && (
        <div
          className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
      )}
      <nav className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <ul className="flex space-x-8">
            {['home', 'about me', 'testimonials', 'portfolio', 'resume', 'capstone'].map((section) => (
              <li key={section}>
                <button
                  onClick={() => setActiveSection(section)}
                  className={`${
                    activeSection === section
                      ? 'text-blue-400 font-medium'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {section}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <main className="relative z-10 max-w-4xl mx-auto px-4">
        <div key={activeSection} className="fade-in">
          {activeSection === 'home' && renderHome()}
          {activeSection === 'about me' && renderAbout()}
          {activeSection === 'testimonials' && renderTestimonials()}
          {activeSection === 'portfolio' && renderPortfolio()}
          {activeSection === 'resume' && renderResume()}
          {activeSection === 'capstone' && renderCapstone()}
        </div>
      </main>
    </div>
  );
};

export default App;
